import type { CreateProjectBody, UpdateProjectBody } from '@ditto/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedClient, useProtectedQuery } from './useApi';

export const queryKeys = {
  all: ['projects'],
  list: () => [...queryKeys.all, 'list'],
  detail: (id: string) => [...queryKeys.all, 'detail', id],
};

export function useListProjectsApi() {
  const { client } = useAuthenticatedClient();

  return useProtectedQuery({
    queryKey: queryKeys.list(),
    queryFn: async () => {
      const response = await client.project.listProjects();
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to fetch projects');
    },
  });
}

export function useGetProjectApi(id: string) {
  const { client } = useAuthenticatedClient();

  return useProtectedQuery({
    queryKey: queryKeys.detail(id),
    queryFn: async () => {
      const response = await client.project.getProject({
        params: { id },
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to fetch project');
    },
    enabled: !!id,
  });
}

export function useCreateProjectApi() {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateProjectBody) => {
      const response = await client.project.createProject({ body: data });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to create project');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.list() });
    },
  });
}

export function useUpdateProjectApi() {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: UpdateProjectBody;
    }) => {
      const response = await client.project.updateProject({
        params: { id },
        body: data,
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to update project');
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.detail(variables.id),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.list() });
    },
  });
}

export function useDeleteProjectApi() {
  const { client } = useAuthenticatedClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await client.project.deleteProject({
        params: { id },
        body: undefined,
      });
      if (response.status === 200) {
        return response.body;
      }
      throw new Error('Failed to delete project');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.list() });
    },
  });
}
